import { IconButton } from '@atlaskit/button/new';
import { cssMap, cx } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import CrossIcon from '@atlaskit/icon/core/cross';
import Image from '@atlaskit/image';
import Link from '@atlaskit/link';
import { Box, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { useMessageInstanceEvent } from '@post-office/message-instance-event-frontend-client';
import { useMessageEvent } from '@post-office/message-lifecycle';

import ChromeIcon from '../../../../../assets/chrome-icon-nobg.svg';
import JiraIcon from '../../../../../assets/jira-icon-nobg.svg';
import { useIntl } from 'react-intl';

import { COPY_VARIATIONS, messages } from '../messages';
import { type JSX } from 'react/jsx-runtime';

const styles = cssMap({
	boxContainer: {
		display: 'flex',
		width: '100%',
		padding: token('space.150', '12px'),
		alignItems: 'flex-start',
		gap: token('space.100', '8px'),
		borderRadius: token('radius.xlarge', '12px'),
		backgroundColor: token('elevation.surface.sunken'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		placeContent: 'center',
	},
	contentStack: {
		gap: token('space.100', '8px'),
		width: '100%',
	},
	headerRow: {
		justifyContent: 'space-between',
		alignItems: 'center',
		width: '100%',
	},
	iconAndTitle: {
		gap: token('space.050', '4px'),
	},
	iconContainer: {
		display: 'flex',
		alignItems: 'center',
		paddingRight: token('space.075', '6px'),
		isolation: 'isolate',
	},
	iconTileBase: {
		display: 'flex',
		alignItems: 'center',
		borderRadius: token('radius.small', '4px'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		// @ts-expect-error - elevation.surface.sunken is not in compiled Box borderColor types but matches Figma spec
		borderColor: token('elevation.surface.sunken'),
	},
	jiraIconTile: {
		justifyContent: 'center',
		padding: token('space.050', '4px'),
		marginRight: token('space.negative.075', '-6px'),
		zIndex: 100,
		overflow: 'clip',
	},
	lastIconTile: {
		padding: token('space.050', '4px'),
		backgroundColor: token('elevation.surface.raised.pressed'),
	},
});

const CHROME_STORE_URL =
	'https://chromewebstore.google.com/detail/rovo-search-chat-and-more/npbjhobkibekbklkghlhfhieggcggpdb';

const getLinkUrl = (cohort: string) => {
	const UTM_CONTENT = `side_nav_install_cta_${cohort}`;
	return `${CHROME_STORE_URL}?utm_source=jira-side-nav&utm_medium=banner&utm_campaign=rovo_extension_side_nav_install_cta&utm_content=${UTM_CONTENT}`;
};

type JiraLeftNavCtaMessageProps = {
	context: {
		experimentCohort: string;
		[key: string]: unknown;
	};
	handleOnDismissed: () => void;
	onLinkClick?: () => void;
};

export const JiraLeftNavCtaMessage = ({
	context,
	handleOnDismissed,
	onLinkClick,
}: JiraLeftNavCtaMessageProps): JSX.Element => {
	const { experimentCohort } = context;

	const { formatMessage } = useIntl();
	const copyVariation =
		COPY_VARIATIONS[experimentCohort as keyof typeof COPY_VARIATIONS] ||
		COPY_VARIATIONS['jira-take-anywhere'];
	const { messageClicked } = useMessageEvent();
	const { messageInstanceClicked } = useMessageInstanceEvent();

	const headingText = formatMessage(copyVariation.heading);
	const descriptionText = formatMessage(copyVariation.description);
	const linkText = formatMessage(
		experimentCohort === 'jira-rovo' ? messages.jiraRovoLinkText : messages.jiraLinkText,
	);
	const jiraIconAlt = formatMessage(messages.jiraIconAlt);
	const chromeIconAlt = formatMessage(messages.chromeIconAlt);
	const dismissLabel = formatMessage(messages.dismissLabel);

	const linkUrl = getLinkUrl(experimentCohort);

	const handleDismiss = () => {
		void messageClicked();
		void messageInstanceClicked({
			messageEventType: 'clicked',
			clickedElement: {
				clickIdentifier: 'dismiss',
				clickedElement: 'button',
				buttonType: 'secondary',
			},
			action: {
				actionType: 'dismiss',
			},
			additionalMetadataFromFrontend: {
				experimentCohort,
				source: 'jira-side-nav',
			},
		});
		handleOnDismissed();
	};

	const handleLinkClick = () => {
		void messageClicked();
		void messageInstanceClicked({
			messageEventType: 'clicked',
			clickedElement: {
				clickIdentifier: 'installLink',
				clickedElement: 'link',
			},
			action: {
				actionType: 'open',
				destinationUrl: linkUrl,
			},
			additionalMetadataFromFrontend: {
				experimentCohort,
				source: 'jira-side-nav',
			},
		});
		onLinkClick?.();
	};

	return (
		<Box xcss={cx(styles.boxContainer)} testId="jira-left-nav-cta">
			<Stack xcss={cx(styles.contentStack)} testId="jira-left-nav-content">
				<Inline xcss={cx(styles.headerRow)}>
					<Inline xcss={cx(styles.iconAndTitle)} alignBlock="center">
						<Box xcss={cx(styles.iconContainer)}>
							<Box
								xcss={cx(styles.iconTileBase, styles.jiraIconTile)}
								style={{ backgroundColor: '#1868db' }}
							>
								<Image src={JiraIcon} alt={jiraIconAlt} width={16} height={16} />
							</Box>
							<Box xcss={cx(styles.iconTileBase, styles.lastIconTile)}>
								<Image src={ChromeIcon} alt={chromeIconAlt} width={16} height={16} />
							</Box>
						</Box>
						<Heading size="xsmall">{headingText}</Heading>
					</Inline>
					<IconButton
						icon={CrossIcon}
						label={dismissLabel}
						appearance="subtle"
						spacing="compact"
						testId="jira-left-nav-cta-dismiss"
						onClick={handleDismiss}
					/>
				</Inline>
				<Text size="small" color="color.text.subtle">
					{descriptionText}
				</Text>
				<Link href={linkUrl} target="blank" onClick={handleLinkClick}>
					{linkText}
				</Link>
			</Stack>
		</Box>
	);
};
